import React from 'react';
import { Drawer, Box, Typography, Avatar, Divider, IconButton, useTheme } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import Navigation from './Navigation';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const { user, profile } = useAuth();

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Toolbar spacer to account for AppBar. flexShrink:0 so a tall nav list
          can never compress it and slide the profile up under the AppBar. */}
      <Box sx={{ minHeight: 64, flexShrink: 0 }} />

      {/* User Profile Section */}
      {open && (
        <Box
          sx={{
            p: 2,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Avatar
            src={profile?.profileImageUrl}
            alt={profile?.displayName || user?.email}
            sx={{
              width: 64,
              height: 64,
              mb: 1,
              bgcolor: 'primary.main',
            }}
          >
            {profile?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ width: '100%' }}>
            {profile?.displayName || user?.given_name || 'Member'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'success.main',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'inline-block',
              }}
            />
            Online
          </Typography>
        </Box>
      )}

      {!open && (
        <Box
          sx={{
            p: 1,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Avatar
            src={profile?.profileImageUrl}
            alt={profile?.displayName || user?.email}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
            }}
          >
            {profile?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
        </Box>
      )}

      <Divider />

      {/* Navigation Items — scroll internally so a long list never pushes the
          profile/spacer up under the AppBar. */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', py: 2 }}>
        <Navigation variant="sidebar" />
      </Box>

      <Divider />

      {/* Toggle Button */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: open ? 'flex-end' : 'center',
        }}
      >
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
